import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { createEnhancementRequest } from 'api'
import pollRequest from 'api/pollRequest'
import Container from 'components/common/Container'
import IdleStage from 'components/stages/enhancement/IdleStage'
import PreviewStage from 'components/stages/enhancement/PreviewStage'
import ProcessingStage from 'components/stages/enhancement/ProcessingStage'
import FinishStage from 'components/stages/enhancement/FinishStage'
import MainLayoutContext from 'contexts/MainLayoutContext'
import { HANDLER_OPTIONS } from 'config/handlers'
import { Rating } from 'types/Rating'
import { OutputFormat } from 'types/OutputFormat'
import { HandlerOption } from 'types/Handlers'
import c from './EnhancementPage.module.scss'

function EnhancementPage() {
  const {
    stage,
    setStage,
    deviceId,
    processedImageUrl,
    setProcessedImageUrl,
    user,
  } = useContext(MainLayoutContext)

  const [handler, setHandler] = useState<HandlerOption>(HANDLER_OPTIONS[0])
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('jpeg')
  const [rating, setRating] = useState<Rating | null>(null)
  const [imageFile, setImageFile] = useState<File | null>(null)

  const imageUrl = useMemo(() => {
    if (imageFile) {
      return URL.createObjectURL(imageFile)
    }
    return null
  }, [imageFile])

  const handleImageChange = useCallback((newFile: File) => {
    setImageFile(newFile)
    setStage('preview')
  }, [])

  const handleProcessingStart = useCallback(async () => {
    setStage('processing')

    const token = user ? await user.getIdToken() : undefined

    const data = await createEnhancementRequest(
      deviceId,
      imageFile!,
      handler.value,
      outputFormat,
      token,
    )
    const requestId = data.request.id

    const poll = await pollRequest(deviceId, requestId)
    setProcessedImageUrl(poll.assets[0].url)
    setStage('finish')
  }, [handler.value, deviceId, imageFile, outputFormat, user])

  const handleEnhanceMore = useCallback((newFile: File) => {
    setImageFile(newFile)
    setProcessedImageUrl(null)
    setRating(null)
    setStage('preview')
  }, [])

  useEffect(() => {
    // Сброс данных, если пользователь вернулся на главный экран
    if (stage !== 'idle') return
    setImageFile(null)
    setProcessedImageUrl(null)
    setRating(null)
  }, [stage])

  useEffect(() => {
    // Изменение допустимых touch-action
    document.documentElement.style.setProperty('--touch-action', 'none')

    return () => {
      document.documentElement.style.setProperty('--touch-action', 'pan-y')
    }
  }, [])

  const mainClasses = useMemo(() => {
    return stage === 'idle' ? `${c.main} ${c.mainWithGradient}` : c.main
  }, [stage])

  return (
    <main className={mainClasses}>
      <Container flex fullHeight fluid={stage === 'finish'}>
        {stage === 'idle' && (
          <IdleStage
            handler={handler}
            handlerOptions={HANDLER_OPTIONS}
            onHandlerChange={setHandler}
            onImageChange={handleImageChange}
          />
        )}
        {stage === 'preview' && (
          <PreviewStage
            imageUrl={imageUrl!}
            outputFormat={outputFormat}
            onOutputFormatChange={setOutputFormat}
            onProcessingStart={handleProcessingStart}
          />
        )}
        {stage === 'processing' && <ProcessingStage imageUrl={imageUrl!} />}
        {stage === 'finish' && (
          <FinishStage
            imageUrl={imageUrl!}
            processedImageUrl={processedImageUrl!}
            rating={rating}
            onRatingChange={setRating}
            onEnhanceMore={handleEnhanceMore}
          />
        )}
      </Container>
    </main>
  )
}

export default EnhancementPage
